import {Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {Title} from '../../../../components/title/title';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {TodoKanban} from '../todo-kanban/todo-kanban';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {AiButton} from '../../../../components/buttons/ai-button/ai-button';
import {Search} from '../../../../components/search/search';
import {TodoStoreService} from '../../services/todo-store-service';
import {TodoQueryService} from '../../services/todo-query.service';
import {TodoCommandService} from '../../services/todo-command.service';
import {Loader} from '../../../../components/loader/loader';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {TodoForm} from '../todo-form/todo-form';
import {ActivatedRoute} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {FilterContainer} from '../../../../components/filter-container/filter-container';
import {FormControl, FormGroup} from '@angular/forms';
import {debounceTime} from 'rxjs';
import {
  ChangeTodoStatusRequest,
  DocumentSummary,
  PartyType,
  TodoCompletion,
  TodoPriorityId,
  TodoState,
  TodoSummary
} from '../../../../../api';
import {TodoFormValue} from '../../models/todo-form-value';
import {mapTodoDetailsToFormValue} from '../../mappers/todo-form.mapper';
import {UserDropdown} from '../../../../components/user-dropdown/user-dropdown';
import {PartyDropdown} from '../../../../components/party-dropdown/party-dropdown';
import {TodoPriorityDropdown} from '../todo-priority-dropdown/todo-priority-dropdown';
import {TodoStateDropdown} from '../todo-state-dropdown/todo-state-dropdown';
import {Popover} from 'primeng/popover';
import {Dot} from '../../../../components/dot/dot';
import {ConfirmService} from '../../../../services/confirm.service';
import {StorageService} from '../../../../services/storage.service';
import {UserStore} from '../../../users/services/user-store';
import {MentionableTodo} from '../todo-comment/todo-comment';
import {TodoFilterParams} from '../../models/todo-filter.params';
import {CurrentUserService} from '../../../../services/current-user.service';
import {AiChat} from '../ai-chat/ai-chat';

@Component({
  selector: 'elcia-todo-home',
  imports: [
    Title, 
    TranslatePipe, 
    TodoKanban, 
    ElciaButton,
    AiButton,
    Search,
    Loader, 
    ElciaDialog, 
    TodoForm, 
    FilterContainer, 
    UserDropdown, 
    PartyDropdown, 
    TodoPriorityDropdown, 
    TodoStateDropdown, 
    Popover,
    Dot,
    AiChat
  ],
  providers: [TodoQueryService, TodoCommandService, TodoStoreService],
  templateUrl: './todo-home.html',
  styleUrl: './todo-home.scss',
})
export class TodoHome {
  private readonly HIGHLIGHT_QUERY_PARAMS = 'highlightTodoId';
  private readonly HIGHLIGHT_CHILD_QUERY_PARAMS = 'highlightChildId';
  private readonly todoStore = inject(TodoStoreService);
  private readonly confirmService = inject(ConfirmService);
  private readonly translateService = inject(TranslateService);
  private readonly storageService = inject(StorageService);
  private readonly userStore = inject(UserStore);
  private readonly currentUserService = inject(CurrentUserService);

  todoForm = viewChild(TodoForm);

  partyTypes = signal<PartyType[]>([PartyType.Company, PartyType.Contact])
  todoFilterForm = new FormGroup({
    searchText: new FormControl('', {nonNullable: true}),
    createdByIds: new FormControl<number[]>([], {nonNullable: true}),
    clientIds: new FormControl<number[]>([], {nonNullable: true}),
    assignedToIds: new FormControl<number[]>([], {nonNullable: true}),
    priorities: new FormControl<TodoPriorityId[]>([], {nonNullable: true}),
    states: new FormControl<TodoState[]>([TodoState.ACTIVE], {nonNullable: true}),
  });

  private readonly filterFormValue = toSignal(
    this.todoFilterForm.valueChanges.pipe(debounceTime(500)),
    {initialValue: this.todoFilterForm.value}
  );

  private readonly HIDDEN_COLUMNS_KEY = 'kanban_hidden_columns';
  readonly hiddenStatusIds = signal<number[]>(
    this.storageService.getItem<number[]>(this.HIDDEN_COLUMNS_KEY, [])
  );
  private readonly _persistHiddenColumns = effect(() => {
    this.storageService.setItem(this.HIDDEN_COLUMNS_KEY, this.hiddenStatusIds());
  });
  readonly todos = computed(() => this.todoStore.todos());
  readonly statuses = computed(() => this.todoStore.statuses());
  readonly users = this.userStore.users;
  readonly storeTodos = computed<MentionableTodo[]>(() =>
    this.todos().map(t => ({id: t.id ?? null, title: t.title ?? null}))
  );

  isFormDisplayed = signal(false);
  isFilterDisplayed = signal(false);
  isAiChatDisplayed = signal(false);
  presetStatusId = signal<number | null>(null);
  isLoading = computed<boolean>(() => this.todoStore.isLoading());
  isFormSaving = computed(() =>
    this.todoStore.createTodoMutation.isPending() ||
    this.todoStore.updateTodoMutation.isPending() ||
    this.todoStore.uploadTodoDocumentMutation.isPending() ||
    this.todoStore.deleteTodoDocumentMutation.isPending()
  );

  selectedTodoDocuments = computed<DocumentSummary[]>(() =>
    this.todoStore.selectedTodo()?.documents ?? []
  );

  // Creation (no selected todo): current user is the author. Edit: compare the todo's author.
  isAuthor = computed<boolean>(() => {
    const todo = this.todoStore.selectedTodo();
    if (!todo) return true;
    return todo.createdBy?.id === this.currentUserService.currentUser()?.id;
  });
  onFilterValueChange = effect(() => {
    this.todoStore.filterParams.set(this.filterFormValue() as TodoFilterParams);
  });
  private readonly queryParams = toSignal(inject(ActivatedRoute).queryParamMap);
  highlightTodoId = computed(() => {
    const id = this.queryParams()?.get(this.HIGHLIGHT_QUERY_PARAMS);
    return id ? Number(id) : null;
  });
  highlightChildId = computed(() => {
    const id = this.queryParams()?.get(this.HIGHLIGHT_CHILD_QUERY_PARAMS);
    return id ? Number(id) : null;
  });

  protected showForm(isVisible: boolean): void {
    this.isFormDisplayed.set(isVisible);
  }

  protected toggleFilterDisplayed(): void {
    this.isFilterDisplayed.update(v => !v);
  }

  protected toggleStatusVisibility(statusId: number): void {
    this.hiddenStatusIds.update(ids =>
      ids.includes(statusId) ? ids.filter(id => id !== statusId) : [...ids, statusId]
    );
  }

  protected onDialogSave(): void {
    this.todoForm()?.submit();
  }

  protected async onFormSubmit(value: TodoFormValue): Promise<void> {
    const form = this.todoForm();

    await this.todoStore.saveTodo({
      selectedId: this.todoStore.selectedTodoId(),
      value,
      pendingFiles: form?.pendingFiles() ?? [],
      pendingDeletionIds: form?.pendingDeletionIds() ?? [],
    });

    form?.resetPendingDocuments();
    this.closeForm();
  }

  isLoadingSelectedTodo = this.todoStore.isLoadingSelectedTodo;
  selectedTodoCompletedAt = computed<Date | null>(() => this.todoStore.selectedTodo()?.completedAt ?? null);

  selectedTodoFormValue = computed<TodoFormValue | undefined>(() => {
    const todo = this.todoStore.selectedTodo();
    if (!todo) return undefined;
    return mapTodoDetailsToFormValue(todo);
  });

  // Edit value (with id) when a todo is selected, otherwise a creation template
  // pre-filled with the column status (no id => POST). undefined when neither applies.
  formInitialValue = computed<TodoFormValue | undefined>(() => {
    const editValue = this.selectedTodoFormValue();
    if (editValue) return editValue;

    const statusId = this.presetStatusId();
    if (statusId === null) return undefined;

    return {
      id: null,
      title: null,
      description: null,
      dueDate: null,
      concernId: null,
      priorityName: TodoPriorityId.UNDEFINED,
      estimatedTimeInDays: null,
      statusId,
      isPrivate: null,
      isDone: false,
      assignedToIds: null,
      children: [],
    };
  });

  protected onStatusChange(event: { todoId: number, request: ChangeTodoStatusRequest }): void {
    this.todoStore.changeTodoStatusMutation.mutate({id: event.todoId, statusId: event.request});
  }

  protected toggleCompletion(todo: TodoSummary): void {
    const newCompletion = todo.completion === TodoCompletion.DONE ? TodoCompletion.PENDING : TodoCompletion.DONE;
    this.todoStore.changeTodoCompletionMutation.mutate({id: todo.id, completion: newCompletion});
  }

  protected toggleChildCompletion(child: TodoSummary): void {
    const newCompletion = child.completion === TodoCompletion.DONE ? TodoCompletion.PENDING : TodoCompletion.DONE;
    this.todoStore.changeTodoCompletionMutation.mutate({id: child.id, completion: newCompletion});
  }

  protected editTodo(todo: TodoSummary): void {
    this.todoStore.selectedTodoId.set(todo.id!);
    this.showForm(true);
  }

  protected addTodoWithStatus(statusId: number): void {
    this.todoStore.selectedTodoId.set(null);
    this.presetStatusId.set(statusId);
    this.showForm(true);
  }

  protected closeForm(): void {
    this.todoForm()?.resetPendingDocuments();
    this.todoStore.selectedTodoId.set(null);
    this.presetStatusId.set(null);
    this.showForm(false);
  }


  protected async deleteTodo() {
    const id = this.selectedTodoFormValue()?.id;
    if (!id) return;

    this.confirmService.confirm({
      title: this.translateService.instant('entity.todo'),
      message: this.selectedTodoFormValue()?.title ?? '',
      accept: async () => {
        await this.todoStore.deleteTodoMutation.mutateAsync(id);
        this.closeForm();
      }
    })
  }
}
